import { Injectable } from '@angular/core';
import {
    FlyFreelyError,
    FlyFreelyLoggingService,
    GqlQueryResult,
    PersonRolesDto,
    PersonService,
    PersonWithRoles
} from '@flyfreely-portal-ui/flyfreely';
import { FormlyGraphQlPaginationService } from '@flyfreely-portal-ui/flyfreely-formly';
import {
    NgSelectPaginationValues,
    NgSelectSearch,
    ngSelectPreSearchConfigure,
    updateNgSelectData
} from '@flyfreely-portal-ui/ng-select';
import { FormatPersonPipe, personSearch } from '@flyfreely-portal-ui/ui';
import { ReplaySubject, Subject } from 'rxjs';
import { debounceTime, take } from 'rxjs/operators';

/**
 * Extended PersonWithRoles interface with formatted name for ng-select display
 */
interface PersonWithFormattedName extends PersonWithRoles {
    name: string;
}

@Injectable()
export class FormlyPilotHelperService implements FormlyGraphQlPaginationService {
    results$ = new ReplaySubject<PersonWithFormattedName[]>(1);
    loading$ = new ReplaySubject<boolean>(1);

    private items: PersonWithFormattedName[] = [];
    private searchSource = new Subject<NgSelectSearch>();
    private organisationId: number;

    private selectConfig: NgSelectPaginationValues = {
        currentPage: 0,
        preSearchPage: null,
        searchValue: null,
        totalItems: null
    };

    searchFn = personSearch.bind(this);

    constructor(
        private personService: PersonService,
        private logging: FlyFreelyLoggingService,
        private formatPersonPipe: FormatPersonPipe
    ) {
        this.loading$.next(false);
    }

    ngOnInit() {
        this.searchSource.pipe(debounceTime(300)).subscribe(search => {
            this.selectConfig = ngSelectPreSearchConfigure(
                search,
                this.selectConfig
            );
            this.refreshItems();
        });
    }

    ngOnDestroy() {
        this.results$.complete();
        this.loading$.complete();
        this.searchSource.complete();
    }

    setOrganisationId(organisationId: number) {
        this.organisationId = organisationId;
    }

    private transformPilot(pilot: PersonWithRoles): PersonWithFormattedName {
        return {
            ...pilot,
            name: this.formatPersonPipe.transform(pilot)
        };
    }

    refreshItems(): () => any {
        if (!this.organisationId) {
            return;
        }

        this.loading$.next(true);

        this.personService
            .findPersonnelGraphQL(
                this.organisationId,
                this.selectConfig.currentPage,
                15,
                {
                    roleOrList: [PersonRolesDto.Roles.PILOT],
                    textSearch: this.selectConfig.searchValue || undefined
                }
            )
            .pipe(take(1))
            .subscribe({
                next: result => {
                    // Filter out duplicates
                    const newPilots = result.results
                        .filter(p => !this.items.find(i => i.id === p.id))
                        .map(p => this.transformPilot(p));

                    this.items = [...this.items, ...newPilots];

                    this.selectConfig = updateNgSelectData(
                        result as GqlQueryResult<any>,
                        this.selectConfig
                    );

                    this.results$.next(this.items);
                    this.loading$.next(false);
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error loading pilots: ${error.message}`
                    );
                    this.loading$.next(false);
                }
            });
    }

    onSelectScroll() {
        const resultLength =
            this.selectConfig.searchValue == null
                ? this.items.length
                : this.items.filter(
                      (i: PersonWithFormattedName) =>
                          i.name
                              .toUpperCase()
                              .indexOf(
                                  this.selectConfig.searchValue.toUpperCase()
                              ) > -1
                  ).length;

        if (
            this.selectConfig.totalItems == null ||
            resultLength < this.selectConfig.totalItems
        ) {
            this.refreshItems();
        }
    }
    onSelectSearch(search: NgSelectSearch) {
        this.searchSource.next(search);
    }

    onSelectOpen() {
        this.resetAndRefresh();
    }

    onSelectClose() {
        this.selectConfig.searchValue = null;
        this.selectConfig.currentPage = 0;
        this.items = [];
    }

    private resetAndRefresh() {
        this.items = [];
        this.selectConfig = {
            currentPage: 0,
            preSearchPage: null,
            searchValue: null,
            totalItems: null
        };
        this.refreshItems();
    }
}
